import type { FC, ReactNode } from 'react'
import type { Project } from '@/types'

import { useEffect, useRef } from 'react'

import {
    motion,
    useAnimationFrame,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform,
    useVelocity
} from 'motion/react'

import { usePrefersReducedMotion } from '@/hooks/use-media-query'
import { Chevron } from '@/components/generals/brand'

interface MarqueeProps {
    projects: Array<Project>
}

const BASE_SPEED = 48

const wrap = (min: number, max: number, value: number): number => {
    const range = max - min

    return ((((value - min) % range) + range) % range) + min
}

// Project names drift sideways; scrolling speeds them up and flips their direction.
export const Marquee: FC<MarqueeProps> = ({ projects = [] }): ReactNode => {
    const groupRef = useRef<HTMLDivElement>(null)
    const groupWidth = useRef(0)
    const direction = useRef(-1)
    const reduced = usePrefersReducedMotion()
    const baseX = useMotionValue(0)
    const { scrollY } = useScroll()
    const scrollVelocity = useVelocity(scrollY)
    const smoothVelocity = useSpring(scrollVelocity, { damping: 50, stiffness: 400 })
    const velocityFactor = useTransform(smoothVelocity, [-1200, 0, 1200], [-4, 0, 4], { clamp: false })
    const x = useTransform(baseX, (value) => `${groupWidth.current ? wrap(-groupWidth.current, 0, value) : 0}px`)

    useEffect(() => {
        const group = groupRef.current

        if (!group) return

        const measure = () => {
            groupWidth.current = group.offsetWidth
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(group)

        return () => observer.disconnect()
    }, [projects.length])

    useAnimationFrame((_, delta) => {
        if (reduced || !groupWidth.current) return

        const factor = velocityFactor.get()

        if (factor < 0) direction.current = 1
        else if (factor > 0) direction.current = -1

        let moveBy = direction.current * BASE_SPEED * (delta / 1000)

        moveBy += moveBy * Math.abs(factor)
        baseX.set(baseX.get() + moveBy)
    })

    if (!projects.length) return null

    return (
        <div aria-hidden='true' className='relative overflow-hidden border-y border-tone-line py-6 md:py-8'>
            <motion.div style={{ x }} className='flex w-max'>
                {[0, 1, 2].map((copy) => (
                    <div key={copy} ref={copy === 0 ? groupRef : undefined} className='flex shrink-0 items-center'>
                        {projects.map((project) => (
                            <span key={`${copy}-${project.id}`} className='flex items-center'>
                                <span
                                    className='whitespace-nowrap px-6 font-heading text-4xl font-bold tracking-[-0.03em] [font-stretch:112%] md:px-10 md:text-6xl'
                                    data-mdlfy-cms={`projektet:${project.id}:emri`}
                                >
                                    {project.emri}
                                </span>

                                <Chevron className='size-5 text-tone-accent md:size-7' />
                            </span>
                        ))}
                    </div>
                ))}
            </motion.div>
        </div>
    )
}
